import { useState, useEffect, useRef } from 'react'
import { HardDrive, Folder, File, Download, Loader2, AlertCircle, Copy, Check, Monitor, ChevronDown, ChevronUp, Package, Trash2, Zap, Clock, Shield, Archive } from 'lucide-react'
import { nasAPI, authAPI } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'

const formatSize = (bytes) => {
  if (!bytes) return '–'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`
}

function ProgressBar({ progress, downloaded, total }) {
  const isIndeterminate = progress === null
  const isDone = progress === 100
  return (
    <div className="space-y-1">
      <div className="relative h-2 w-full rounded-full bg-white/10 overflow-hidden">
        {isIndeterminate
          ? <div className="absolute inset-0 rounded-full bg-neon-green/70 animate-pulse" />
          : <div className="h-full rounded-full bg-neon-green transition-all duration-200" style={{ width: `${progress}%` }} />
        }
      </div>
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>{isIndeterminate ? 'Downloaden...' : isDone ? 'Verwerken...' : `${progress}%`}</span>
        <span>{total ? `${formatSize(downloaded)} / ${formatSize(total)}` : formatSize(downloaded)}</span>
      </div>
    </div>
  )
}

function GameCard({ folder, isAdmin, cacheInfo, onCacheCleared, onStartPolling }) {
  const [expanded, setExpanded] = useState(false)
  const [files, setFiles] = useState([])
  const [loadingFiles, setLoadingFiles] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [downloadProgress, setDownloadProgress] = useState(null)
  const [downloadedBytes, setDownloadedBytes] = useState(0)
  const [totalBytes, setTotalBytes] = useState(null)
  const [downloadError, setDownloadError] = useState(null)
  const [copied, setCopied] = useState(false)
  const [clearingCache, setClearingCache] = useState(false)
  const [precaching, setPrecaching] = useState(false)
  const [fileProgress, setFileProgress] = useState({})

  const folderSafeName = folder.name.replace(/[^a-zA-Z0-9-_.]/g, '_')
  const cached = cacheInfo?.cached?.find(c => c.name === folderSafeName)
  const isGenerating = cacheInfo?.generating?.includes(folder.path)

  const loadFiles = async () => {
    setLoadingFiles(true)
    try {
      const res = await nasAPI.browseGames(folder.path)
      setFiles(res.data.items || [])
    } catch {
      setFiles([])
    } finally {
      setLoadingFiles(false)
    }
  }

  const toggleExpand = () => {
    if (!expanded) loadFiles()
    setExpanded(!expanded)
  }

  const handleDownloadFolder = async () => {
    setDownloading(true)
    setDownloadedBytes(0)
    setTotalBytes(null)
    setDownloadProgress(null)
    setDownloadError(null)

    try {
      const token = localStorage.getItem('auth_token')
      const url = `/api/nas/games/download-folder?path=${encodeURIComponent(folder.path)}`
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } })

      if (res.status === 503) { setDownloadError((await res.json()).error); return }
      if (!res.ok) { setDownloadError((await res.json().catch(() => ({}))).error || `HTTP ${res.status}`); return }

      const total = parseInt(res.headers.get('Content-Length') || '0', 10) || cached?.size || null
      setTotalBytes(total)
      setDownloadProgress(total ? 0 : null)

      const reader = res.body.getReader()
      const chunks = []
      let received = 0

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        chunks.push(value)
        received += value.length
        setDownloadedBytes(received)
        if (total) setDownloadProgress(Math.round((received / total) * 100))
      }

      // Zet op 100% en yield naar event loop zodat React de knop kan renderen
      setDownloadProgress(100)
      await new Promise(r => setTimeout(r, 50))

      const blob = new Blob(chunks)
      const objectUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = objectUrl
      a.download = `${folder.name}.zip`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(objectUrl)

      // Houd "Verwerken" zichtbaar: 1s per 100MB, min 2s, max 15s
      const delayMs = Math.min(15000, Math.max(2000, (received / (1024 * 1024)) / 100 * 1000))
      await new Promise(r => setTimeout(r, delayMs))

      onCacheCleared()
    } catch (e) {
      setDownloadError(`Download mislukt: ${e.message}`)
    } finally {
      setDownloading(false)
      setDownloadProgress(null)
    }
  }

  const handlePrecache = async () => {
    setPrecaching(true)
    try {
      await nasAPI.precacheGame(folder.path)
      await onCacheCleared()
      onStartPolling()
    } catch {
      alert('Pre-cache starten mislukt')
    } finally {
      setPrecaching(false)
    }
  }

  const handleClearCache = async () => {
    if (!confirm(`Cache van "${folder.name}" wissen? De volgende download hergenereert de ZIP.`)) return
    setClearingCache(true)
    try {
      await nasAPI.clearGameCache(folder.path)
      onCacheCleared()
    } catch {
      alert('Cache wissen mislukt')
    } finally {
      setClearingCache(false)
    }
  }

  const handleDownloadFile = async (file) => {
    setFileProgress(p => ({ ...p, [file.path]: { pct: null, received: 0, total: null } }))
    try {
      const token = localStorage.getItem('auth_token')
      const url = `/api/nas/games/download?path=${encodeURIComponent(file.path)}`
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)

      const total = parseInt(res.headers.get('Content-Length') || '0', 10) || file.size || null
      const reader = res.body.getReader()
      const chunks = []
      let received = 0

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        chunks.push(value)
        received += value.length
        setFileProgress(p => ({ ...p, [file.path]: { pct: total ? Math.round((received / total) * 100) : null, received, total } }))
      }

      const blob = new Blob(chunks)
      const objectUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = objectUrl
      a.download = file.name
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(objectUrl)
    } catch (e) {
      alert(`Download mislukt: ${e.message}`)
    } finally {
      setFileProgress(p => { const n = { ...p }; delete n[file.path]; return n })
    }
  }

  const copySmbPath = () => {
    navigator.clipboard.writeText(`\\\\${window.location.hostname}\\game_files\\${folder.name}`).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <div className="rounded-lg border border-neon bg-card overflow-hidden shadow-lg flex flex-col">
      <div className="p-5 flex flex-col gap-3">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-neon-blue/10 flex items-center justify-center">
            <Package className="h-5 w-5 text-neon-blue" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-bold text-lg leading-tight truncate">{folder.name}</h3>
            <div className="flex items-center gap-2 mt-0.5">
              {isGenerating ? (
                <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30 text-xs">
                  <Loader2 className="h-3 w-3 animate-spin mr-1" /> Wordt gegenereerd...
                </Badge>
              ) : cached ? (
                <Badge className="bg-neon-green/20 text-neon-green border-neon-green/30 text-xs">
                  <Zap className="h-3 w-3 mr-1" /> Gecached · {formatSize(cached.size)}
                </Badge>
              ) : (
                <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30 text-xs">
                  <Clock className="h-3 w-3 mr-1" /> Eerste download duurt even
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* Error */}
        {downloadError && (
          <p className="text-xs text-red-400 bg-red-500/10 rounded px-2 py-1">{downloadError}</p>
        )}

        {/* Download progress */}
        {downloading && (
          <ProgressBar progress={downloadProgress} downloaded={downloadedBytes} total={totalBytes} />
        )}

        {/* Actions */}
        <div className="flex gap-2">
          <Button
            onClick={handleDownloadFolder}
            disabled={downloading || isGenerating}
            className={`flex-1 border transition-all disabled:opacity-40 ${
              downloadProgress === 100
                ? 'bg-orange-500/10 hover:bg-orange-500/20 text-orange-400 border-orange-500/30'
                : 'bg-neon-green/10 hover:bg-neon-green/20 text-neon-green border-neon-green/30 shadow-neon-green hover:shadow-neon-green-lg'
            }`}
          >
            {downloadProgress === 100
              ? <><Loader2 className="h-4 w-4 animate-spin" /> Verwerken...</>
              : downloading
                ? <><Loader2 className="h-4 w-4 animate-spin" />{cached ? ' Downloaden...' : ' Genereren & downloaden...'}</>
                : <><Download className="h-4 w-4" /> Download als ZIP</>
            }
          </Button>

          {isAdmin && !cached && !isGenerating && (
            <Button
              variant="ghost"
              size="icon"
              onClick={handlePrecache}
              disabled={precaching}
              className="text-muted-foreground hover:text-neon-blue hover:bg-neon-blue/10"
              title="Pre-cache ZIP genereren op NAS"
            >
              {precaching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Archive className="h-4 w-4" />}
            </Button>
          )}

          {isAdmin && cached && (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClearCache}
              disabled={clearingCache}
              className="text-muted-foreground hover:text-red-500 hover:bg-red-500/10"
              title="Cache wissen"
            >
              {clearingCache ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            </Button>
          )}

          <Button
            variant="ghost"
            size="icon"
            onClick={toggleExpand}
            className="text-muted-foreground hover:text-foreground"
            title="Bekijk bestanden"
          >
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {/* Expanded file list */}
      {expanded && (
        <div className="border-t border-border/50 bg-background/30">
          {loadingFiles ? (
            <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Laden...
            </div>
          ) : files.length === 0 ? (
            <div className="p-4 text-sm text-muted-foreground text-center">Geen bestanden gevonden</div>
          ) : (
            <ul className="divide-y divide-border/30">
              {files.map((item) => (
                <li key={item.path} className="flex items-center gap-3 px-5 py-2.5 hover:bg-neon-blue/5 group">
                  {item.type === 'dir'
                    ? <Folder className="h-4 w-4 text-yellow-400 flex-shrink-0" />
                    : <File className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  }
                  <span className="flex-1 text-sm truncate">{item.name}</span>
                  {item.type === 'file' && (
                    <>
                      {fileProgress[item.path] ? (
                        <div className="flex-1 max-w-[140px]">
                          <ProgressBar
                            progress={fileProgress[item.path].pct}
                            downloaded={fileProgress[item.path].received}
                            total={fileProgress[item.path].total}
                          />
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground flex-shrink-0">{formatSize(item.size)}</span>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDownloadFile(item)}
                        disabled={!!fileProgress[item.path]}
                        className="h-6 w-6 opacity-0 group-hover:opacity-100 text-neon-blue hover:bg-neon-blue/10 transition-all"
                      >
                        {fileProgress[item.path]
                          ? <Loader2 className="h-3 w-3 animate-spin" />
                          : <Download className="h-3 w-3" />
                        }
                      </Button>
                    </>
                  )}
                  {item.type === 'dir' && (
                    <Badge variant="outline" className="text-xs py-0 border-yellow-400/40 text-yellow-400">map</Badge>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}

export default function NasGamesPage() {
  const [games, setGames] = useState([])
  const [smbPath, setSmbPath] = useState('')
  const [cacheInfo, setCacheInfo] = useState({ cached: [], generating: [] })
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [currentUser, setCurrentUser] = useState(null)
  const [openingAdmin, setOpeningAdmin] = useState(false)

  const pollRef = useRef(null)

  const fetchCacheInfo = async () => {
    try {
      const res = await nasAPI.getCacheInfo()
      setCacheInfo(res.data)
      return res.data
    } catch {}
  }

  // Poll every 5s while any ZIP is still being generated
  const startPolling = () => {
    if (pollRef.current) return
    pollRef.current = setInterval(async () => {
      const data = await fetchCacheInfo()
      if (!data || data.generating?.length === 0) {
        clearInterval(pollRef.current)
        pollRef.current = null
      }
    }, 5000)
  }

  useEffect(() => () => clearInterval(pollRef.current), [])

  useEffect(() => {
    const load = async () => {
      try {
        const [gamesRes, userRes] = await Promise.all([
          nasAPI.browseGames(''),
          authAPI.getCurrentUser().catch(() => ({ data: { user: null } }))
        ])
        const folders = (gamesRes.data.items || []).filter(i => i.type === 'dir')
        setGames(folders)
        setSmbPath(gamesRes.data.smb || '')
        setCurrentUser(userRes.data.user)
        await fetchCacheInfo()
      } catch (err) {
        setError(err.response?.data?.error || 'Kan game bestanden niet laden van de NAS')
      } finally {
        setIsLoading(false)
      }
    }
    load()
  }, [])

  const isAdmin = ['admin','superadmin'].includes(currentUser?.role)

  const openSmbAdmin = () => {
    setOpeningAdmin(true)
    const token = localStorage.getItem('auth_token')
    fetch('/api/nas/games/smb-admin-link', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.setAttribute('download', 'game_files_admin.url')
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      })
      .finally(() => setTimeout(() => setOpeningAdmin(false), 1500))
  }

  const cachedCount = cacheInfo.cached?.length || 0

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-neon-blue to-neon-purple flex items-center justify-center flex-shrink-0">
            <HardDrive className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Game Bestanden</h1>
            <p className="text-muted-foreground text-sm">Download game bestanden van de NAS</p>
          </div>
        </div>
        {cachedCount > 0 && (
          <Badge className="bg-neon-green/20 text-neon-green border-neon-green/30">
            <Zap className="h-3 w-3 mr-1" /> {cachedCount} gecached
          </Badge>
        )}
      </div>

      {/* SMB Admin knop — enkel zichtbaar voor admins */}
      {smbPath && isAdmin && (
        <div className="p-4 rounded-lg border border-orange-500/20 bg-orange-500/5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Shield className="h-4 w-4 text-orange-400 shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-orange-400">Admin toegang · NAS Verkenner</p>
              <p className="text-xs text-muted-foreground truncate">Opent game map met schrijfrechten via <code>{smbPath}</code></p>
            </div>
          </div>
          <Button
            size="sm"
            onClick={openSmbAdmin}
            disabled={openingAdmin}
            className="shrink-0 bg-orange-500/10 hover:bg-orange-500/20 text-orange-400 border border-orange-500/30"
          >
            {openingAdmin
              ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Openen...</>
              : <><Monitor className="h-3 w-3 mr-1" /> Open als Admin</>}
          </Button>
        </div>
      )}

      {error && (
        <Alert variant="destructive" className="border-red-500/50 bg-red-500/10">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-neon-blue" />
          <span className="ml-3 text-muted-foreground">Laden van NAS...</span>
        </div>
      )}

      {!isLoading && !error && games.length === 0 && (
        <div className="rounded-lg border border-neon bg-card/50 text-center py-16">
          <HardDrive className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-30" />
          <h3 className="text-xl font-semibold mb-2">Geen game mappen gevonden</h3>
          <p className="text-muted-foreground text-sm">
            Zet game bestanden in mappen op de NAS share <code className="text-neon-blue">{smbPath}</code>
          </p>
        </div>
      )}

      {!isLoading && games.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {games.map((folder) => (
            <GameCard
              key={folder.path}
              folder={folder}
              isAdmin={isAdmin}
              cacheInfo={cacheInfo}
              onCacheCleared={fetchCacheInfo}
              onStartPolling={startPolling}
            />
          ))}
        </div>
      )}
    </div>
  )
}
